import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Modal,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  FscLabelApiError,
  FscLabelRow,
  FscScope,
  FscStatus,
  createFscLabel,
  deleteFscLabel,
  getFscLabels,
  updateFscLabel,
} from '../../api/fsc_label';
import { getGHGStandards } from '../../api/ghg';

const { Text } = Typography;
const { TextArea } = Input;

const QUERY_KEY = 'fscLabels';
// Only the projects that need an FSC review; the status chips narrow that list.
const SCOPE: FscScope = 'required';
const SEARCH_MAX_LENGTH = 200;
const NOTE_MAX_LENGTH = 2000;
const SEARCH_DEBOUNCE_MS = 400;

const STATUS_META: Record<FscStatus, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'gold' },
  certified: { label: 'FSC certified', color: 'green' },
  not_certified: { label: 'Not FSC certified', color: 'red' },
};

type StatusFilter = 'all' | Exclude<FscStatus, 'not_certified'>;

type Dialog = { mode: 'create'; row: FscLabelRow } | { mode: 'edit'; row: FscLabelRow };

const formatWhen = (value: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

/**
 * The server matches code plus id as one string ("ACR840"), so "ACR 840" finds nothing.
 * Close the gap when the first word is a real registry code and the second is an id.
 * A code is required so that a project name such as "Pine 2" is still sent as typed.
 */
const joinCodeAndId = (text: string, codes: string[]) => {
  const match = text.match(/^(\S+)\s+(\d+)$/);
  return match && codes.includes(match[1].toUpperCase()) ? `${match[1]}${match[2]}` : text;
};

const dash = <Text type="secondary">—</Text>;

const ellipsisText = (value: string | null) =>
  value ? <Text ellipsis={{ tooltip: value }}>{value}</Text> : dash;

// Wrapped rather than truncated, for the long values a reviewer reads in full.
const wrappedText = (value: string | null) =>
  value ? <Text style={{ wordBreak: 'break-word' }}>{value}</Text> : dash;

/**
 * Create marks the project FSC certified; edit only changes the note. The note is
 * optional on create and clearable on update, so an empty box means "no note" (create)
 * or "clear it" (update, sent as null).
 */
function FscLabelModal({
  dialog,
  confirmLoading,
  onCancel,
  onSubmit,
}: {
  dialog: Dialog;
  confirmLoading: boolean;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const { row } = dialog;
  const isEdit = dialog.mode === 'edit';
  const initialNote = row.note ?? '';
  const [note, setNote] = useState(initialNote);

  // An update needs at least one field, so there is nothing to send until the note changes.
  const unchanged = isEdit && note.trim() === initialNote.trim();

  const title = isEdit
    ? `Edit FSC label — ${row.project_key}`
    : `Mark ${row.project_key} as FSC certified`;

  return (
    <Modal
      open
      title={title}
      okText={isEdit ? 'Save' : 'Confirm'}
      okButtonProps={{ disabled: unchanged }}
      confirmLoading={confirmLoading}
      onCancel={onCancel}
      onOk={() => onSubmit(note.trim())}
      maskClosable={!confirmLoading}
      destroyOnClose
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Text type="secondary">
          This changes the project's Apple Spec status in Supply Bridge immediately.
        </Text>
        {row.project_name && <Text strong>{row.project_name}</Text>}
        {row.existing_certificate && (
          <Text type="secondary">Existing certificate on record: {row.existing_certificate}</Text>
        )}
        <div>
          <Text type="secondary">Internal note (optional), e.g. the FSC certificate number</Text>
          <TextArea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="FSC-C000001"
            maxLength={NOTE_MAX_LENGTH}
            showCount
            autoSize={{ minRows: 2, maxRows: 6 }}
            style={{ marginTop: 4 }}
          />
        </div>
      </Space>
    </Modal>
  );
}

function FscLabelTab() {
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  // project_id of the row whose "Remove this label?" confirmation is open, if any.
  const [removeOpenFor, setRemoveOpenFor] = useState<string | null>(null);

  // Wait for a pause in typing so each keystroke is not a request, and restart from the
  // first page because the old page number means nothing for a different result set.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchText(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Shares its cache key with the other mapper tabs, so this is usually already loaded.
  const { data: standardsData } = useQuery(['ghgStandards'], getGHGStandards);
  const registryCodes: string[] = (
    standardsData?.response ?? (Array.isArray(standardsData) ? standardsData : [])
  )
    .map((standard: any) => String(standard.code ?? '').toUpperCase())
    .filter(Boolean);

  const params = {
    scope: SCOPE,
    status: statusFilter === 'all' ? undefined : statusFilter,
    search_text: joinCodeAndId(searchText, registryCodes) || undefined,
    page,
    limit,
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    [QUERY_KEY, params],
    () => getFscLabels(params),
    { keepPreviousData: true },
  );

  // Removing or re-tagging the last row on the last page leaves that page empty while
  // total_count still has earlier pages; step back to the last one that has rows.
  useEffect(() => {
    if (data && !isFetching && data.data.length === 0 && page > 1) {
      setPage(Math.max(1, Math.ceil(data.total_count / limit)));
    }
  }, [data, isFetching, page, limit]);

  const refetchList = () => queryClient.invalidateQueries(QUERY_KEY);

  // The list is refetched after every action, whether it worked or not: a 409 or 404
  // means the row changed under us, and the refetch is what shows the current state.
  const onMutationError = (err: any) => {
    message.error(err?.message || 'Something went wrong');
  };

  const createMutation = useMutation(createFscLabel, {
    onSuccess: () => {
      message.success('FSC label saved');
      setDialog(null);
    },
    onError: (err: any) => {
      if (err instanceof FscLabelApiError && err.status === 409) {
        message.warning('This project already has a label. The list has been refreshed.');
        setDialog(null);
      } else {
        onMutationError(err);
      }
    },
    onSettled: refetchList,
  });

  const updateMutation = useMutation(
    ({ id, ...payload }: { id: string; note: string | null }) =>
      updateFscLabel(id, payload),
    {
      onSuccess: () => {
        message.success('FSC label updated');
        setDialog(null);
      },
      onError: (err: any) => {
        if (err instanceof FscLabelApiError && err.status === 404) {
          message.warning('This label no longer exists. The list has been refreshed.');
          setDialog(null);
        } else {
          onMutationError(err);
        }
      },
      onSettled: refetchList,
    },
  );

  const deleteMutation = useMutation(deleteFscLabel, {
    onSuccess: () => {
      message.success('FSC label removed; the project is pending again');
    },
    onError: (err: any) => {
      if (err instanceof FscLabelApiError && err.status === 404) {
        message.warning('This label was already removed. The list has been refreshed.');
      } else {
        onMutationError(err);
      }
    },
    onSettled: refetchList,
  });

  const handleSubmit = (note: string) => {
    if (!dialog) return;
    const { row } = dialog;

    if (dialog.mode === 'create') {
      createMutation.mutate({
        project_id: row.project_id,
        is_fsc_certified: true,
        ...(note ? { note } : {}),
      });
      return;
    }

    if (!row.id) return;
    // An empty note is sent as null to clear it.
    updateMutation.mutate({ id: row.id, note: note || null });
  };

  const handleStatusChange = (next: StatusFilter) => {
    setStatusFilter(next);
    setPage(1);
  };

  const columns: any[] = [
    {
      title: 'Project',
      key: 'project',
      width: 260,
      render: (_: any, row: FscLabelRow) => (
        <div style={{ minWidth: 0 }}>
          <Text strong>{row.project_key}</Text>
          <br />
          {wrappedText(row.project_name)}
        </div>
      ),
    },
    {
      title: 'Project type',
      dataIndex: 'project_type',
      key: 'project_type',
      width: 170,
      render: ellipsisText,
    },
    {
      title: 'Methodology',
      dataIndex: 'methodology',
      key: 'methodology',
      width: 220,
      render: wrappedText,
    },
    {
      title: 'Existing certificate',
      dataIndex: 'existing_certificate',
      key: 'existing_certificate',
      width: 160,
      render: ellipsisText,
    },
    {
      title: 'FSC status',
      dataIndex: 'fsc_status',
      key: 'fsc_status',
      width: 150,
      render: (status: FscStatus) => {
        const meta = STATUS_META[status];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : <Tag>{status}</Tag>;
      },
    },
    {
      title: 'Internal Note',
      dataIndex: 'note',
      key: 'note',
      width: 200,
      // The note is typed in a multi-line box, so keep the line breaks it was written with.
      render: (note: string | null) =>
        note ? <Text style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{note}</Text> : dash,
    },
    {
      title: 'Updated',
      key: 'updated',
      width: 180,
      render: (_: any, row: FscLabelRow) =>
        row.updated_at || row.updated_by ? (
          <div style={{ minWidth: 0 }}>
            {ellipsisText(row.updated_by)}
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatWhen(row.updated_at)}
            </Text>
          </div>
        ) : (
          dash
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 200,
      fixed: 'right' as const,
      // Each pinned cell is its own stacking context (sticky + z-index), so the popup,
      // which lives inside this cell, is painted over by the buttons of the rows below it.
      // Lift the cell whose confirmation is open above the other pinned cells.
      onCell: (row: FscLabelRow) => ({
        style: removeOpenFor === row.project_id ? { zIndex: 10 } : undefined,
      }),
      render: (_: any, row: FscLabelRow) => {
        if (!row.id) {
          return (
            <Button
              size="small"
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setDialog({ mode: 'create', row })}
            >
              Mark FSC certified
            </Button>
          );
        }
        return (
          <Space size={8}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => setDialog({ mode: 'edit', row })}
            >
              Edit
            </Button>
            <Popconfirm
              title="Remove this FSC label?"
              description="The project goes back to pending and its Apple Spec status changes immediately."
              // The default is a popup on <body>, positioned from the button's page
              // coordinates, so it can drift away from the row (a long page, a pinned
              // column in a horizontally scrolling table). Mount it in the row's own cell so
              // it stays with the record. It opens to the left because the table body clips
              // overflow vertically, and a popup above or below the first or last row would
              // be cut off.
              getPopupContainer={(trigger) =>
                (trigger.closest('.ant-table-cell') as HTMLElement | null) ?? document.body
              }
              placement="left"
              // The popup is mounted in a narrow cell, so with no width of its own it is first
              // squeezed to the cell, then grows once placed to the left and has to be moved
              // again, which shows up as it sliding across. A fixed width keeps its size, and so
              // its position, the same from the first frame. Auto-flip is off for the same
              // reason: flipping to the right would put it outside the table, where it is clipped.
              overlayStyle={{ width: 420 }}
              autoAdjustOverflow={false}
              onOpenChange={(open) => setRemoveOpenFor(open ? row.project_id : null)}
              okText="Remove"
              okButtonProps={{ danger: true }}
              onConfirm={() => deleteMutation.mutate(row.id as string)}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleteMutation.isLoading && deleteMutation.variables === row.id}
              >
                Remove
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const statusChips: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'certified', label: 'Certified' },
  ];

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space wrap size={12}>
          <Space size={4} wrap>
            {statusChips.map((chip) => (
              <Tag.CheckableTag
                key={chip.key}
                checked={statusFilter === chip.key}
                onChange={() => handleStatusChange(chip.key)}
              >
                {chip.label}
              </Tag.CheckableTag>
            ))}
          </Space>
        </Space>
        <Space wrap>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search name, registry id or e.g. CAR1573"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            maxLength={SEARCH_MAX_LENGTH}
            allowClear
            style={{ width: 320 }}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={isFetching && !isLoading}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={(error as Error).message || 'Could not load FSC labels'}
          action={
            <Button size="small" onClick={() => refetch()}>
              Retry
            </Button>
          }
        />
      ) : null}

      <Table
        columns={columns}
        dataSource={data?.data ?? []}
        rowKey="project_id"
        size="middle"
        tableLayout="fixed"
        loading={isLoading || isFetching}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.total_count ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100, 200],
          showTotal: (total, [from, to]) => `${from}–${to} of ${total}`,
          onChange: (nextPage, nextSize) => {
            // A new page size invalidates the page number, so go back to the first.
            if (nextSize !== limit) {
              setLimit(nextSize);
              setPage(1);
            } else {
              setPage(nextPage);
            }
          },
        }}
      />

      {dialog && (
        <FscLabelModal
          // A different row or mode must start from its own values, not the last one's.
          key={`${dialog.mode}-${dialog.row.project_id}`}
          dialog={dialog}
          confirmLoading={createMutation.isLoading || updateMutation.isLoading}
          onCancel={() => setDialog(null)}
          onSubmit={handleSubmit}
        />
      )}
    </div>
  );
}

export default FscLabelTab;
